import React from "react";
import { Alert, Box, Button, Chip, Stack, Typography } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { useNavigate, useParams } from "react-router-dom";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import DashboardLoadingState from "../../../components/dashboard/DashboardLoadingState";
import OwnerTaskDialog from "./calendar/OwnerTaskDialog";
import { idOf, surfaceSx } from "./ownerUi";
import OwnerProjectAgreementPanel from "./components/OwnerProjectAgreementPanel";

function todayKey() { const date = new Date(); const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60000); return shifted.toISOString().slice(0, 10); }

// WP-STANDARD | PROJECT 360 FUNCTIONAL JSON EXPORT
// Status: READ-ONLY · SECRET-SAFE · CLIENT-SNAPSHOT · v1
// Exports exactly what Project 360 already loaded so debugging invoices, contract
// allocations and historical payments never depends on the rendered HTML alone.
const PROJECT_EXPORT_SECRET_KEY = /(password|passwd|secret|token|credential|authorization|cookie|api[_-]?key|client[_-]?secret|access[_-]?token|refresh[_-]?token)/i;
function safeProjectExport(value, seen = new WeakSet()) {
  if (value == null || typeof value !== "object") return value;
  if (seen.has(value)) return "[CIRCULAR]";
  seen.add(value);
  if (Array.isArray(value)) return value.map((item) => safeProjectExport(item, seen));
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [
    key,
    PROJECT_EXPORT_SECRET_KEY.test(key) ? "[REDACTED]" : safeProjectExport(item, seen),
  ]));
}
function downloadProjectJson({ initiativeId, context, agreement }) {
  const projectName = context?.initiative?.name || agreement?.initiative?.name || "proyecto";
  const generatedAt = new Date().toISOString();
  const payload = {
    schema: "webprofit.owner.project-export",
    schemaVersion: 1,
    generatedAt,
    purpose: "Snapshot funcional de Proyecto 360 para auditar contrato, cuotas, mensualidades, pagos, saldos y documentos derivados.",
    safety: { readOnly: true, secretsIncluded: false, secretsPolicy: "Claves sensibles redactadas por nombre de campo." },
    initiativeId,
    projectName,
    sourceView: "OwnerProject360Page",
    context: safeProjectExport(context),
    agreement: safeProjectExport(agreement),
    diagnostics: {
      commitmentCount: Number(context?.commitments?.items?.length || 0),
      termCount: Number(agreement?.terms?.length || 0),
      paymentPlanCount: Number(agreement?.paymentPlans?.length || 0),
      paymentCount: Number(agreement?.payments?.length || 0),
      hasBaseContract: Boolean(agreement?.financial?.baseContract),
      hasMonthlyAgreement: Boolean(agreement?.financial?.monthlyAgreement),
      hasCombinedCollectionInvoice: Boolean(agreement?.financial?.collectionInvoice),
    },
  };
  const text = JSON.stringify(payload, null, 2);
  const blob = new Blob([text], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  const slug = String(projectName || "proyecto").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase() || "proyecto";
  anchor.href = url;
  anchor.download = `webprofit-proyecto-${slug}-${generatedAt.replace(/[:.]/g, "-")}.json`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function OwnerProject360Page() {
  const { initiativeId } = useParams();
  const navigate = useNavigate();
  const [context, setContext] = React.useState(null);
  const [agreement, setAgreement] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [taskOpen, setTaskOpen] = React.useState(false);
  const [updating, setUpdating] = React.useState("");

  const load = React.useCallback(async () => {
    setError("");
    try {
      const [contextResponse, agreementResponse] = await Promise.all([
        ownerApi.initiativeContext(initiativeId),
        ownerApi.projectAgreement(initiativeId),
      ]);
      setContext(contextResponse?.context || null);
      setAgreement(agreementResponse?.agreement || null);
    }
    catch (err) { setError(formatApiError(err, "No pude cargar el proyecto.")); }
    finally { setLoading(false); }
  }, [initiativeId]);
  React.useEffect(() => { load(); }, [load]);

  if (loading && !context) return <DashboardLoadingState label="Cargando proyecto…" minHeight={420} />;
  const project = context?.initiative;
  const client = context?.client;
  const tasks = context?.commitments?.items || [];
  const openTasks = tasks.filter((row) => row.status === "open");
  const complete = async (row) => { setUpdating(idOf(row)); setError(""); try { await ownerApi.updateCommitment(idOf(row), { status: "done" }); await load(); } catch (err) { setError(formatApiError(err, "No pude completar la tarea.")); } finally { setUpdating(""); } };
  const discard = async (row) => { setUpdating(idOf(row)); setError(""); try { await ownerApi.deleteCommitment(idOf(row)); await load(); } catch (err) { setError(formatApiError(err, "No pude descartar el pendiente.")); } finally { setUpdating(""); } };

  return <Box sx={{ width: "100%", maxWidth: 1100, mx: "auto" }}>
    <Button size="small" startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/dashboard/owner/crm?vista=proyectos")} sx={{ mb: .65, textTransform: "none", fontWeight: 900 }}>CRM</Button>
    {error ? <Alert severity="error" sx={{ mb: .7 }}>{error}</Alert> : null}
    {project ? <Stack spacing={.7}>
      <Box sx={{ ...surfaceSx, p: { xs: 1, sm: 1.35 } }}><Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} alignItems={{ sm: "center" }}><Box sx={{ minWidth: 0 }}><Typography sx={{ fontSize: { xs: "1.25rem", sm: "1.55rem" }, fontWeight: 1000 }}>{project.name}</Typography><Typography sx={{ color: "text.secondary", fontSize: ".76rem", mt: .2 }}>{client?.name || client?.company || "Sin cliente vinculado"}</Typography><Stack direction="row" spacing={.4} sx={{ mt: .6 }}><Chip size="small" label={project.status || "active"} sx={{ fontWeight: 850 }} /><Chip size="small" variant="outlined" label={project.priority || "normal"} sx={{ fontWeight: 850 }} /></Stack></Box><Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap><Button size="small" variant="outlined" startIcon={<DownloadRoundedIcon />} onClick={() => downloadProjectJson({ initiativeId, context, agreement })} sx={{ textTransform: "none", fontWeight: 950 }}>JSON proyecto</Button><Button variant="contained" startIcon={<AddTaskRoundedIcon />} onClick={() => setTaskOpen(true)} sx={{ textTransform: "none", fontWeight: 950 }}>Tarea</Button></Stack></Stack></Box>
      {project.outcome ? <Box sx={{ ...surfaceSx, p: 1 }}><Typography sx={{ fontSize: ".78rem" }}><b>Resultado:</b> {project.outcome}</Typography></Box> : null}
      <Box sx={{ ...surfaceSx, p: 1 }}><Typography sx={{ fontSize: ".68rem", color: "text.secondary", fontWeight: 950, mb: .55 }}>PENDIENTES ÚNICOS · {openTasks.length}</Typography><Stack spacing={.3}>{openTasks.length ? openTasks.map((row) => <Box key={idOf(row)} sx={{ display: "flex", alignItems: "center", gap: .6, py: .55, px: .65, borderRadius: 1.6, bgcolor: "action.hover" }}><Box sx={{ flex: 1, minWidth: 0 }}><Typography noWrap sx={{ fontWeight: 900, fontSize: ".78rem" }}>{row.title || row.name || "Tarea"}</Typography></Box><Button size="small" disabled={updating === idOf(row)} onClick={() => complete(row)} startIcon={<CheckCircleRoundedIcon />} sx={{ textTransform: "none", fontWeight: 850 }}>Hecho</Button><Button size="small" color="inherit" disabled={updating === idOf(row)} onClick={() => discard(row)} startIcon={<DeleteOutlineRoundedIcon />} sx={{ textTransform: "none", fontWeight: 850 }}>Descartar</Button></Box>) : <Typography sx={{ color: "text.secondary", fontSize: ".76rem", py: 1 }}>No hay pendientes abiertos.</Typography>}</Stack></Box>
      <OwnerProjectAgreementPanel agreement={agreement} initiativeId={initiativeId} onReload={load} onError={setError} />
    </Stack> : null}
    <OwnerTaskDialog open={taskOpen} onClose={() => setTaskOpen(false)} onSaved={async () => { setTaskOpen(false); await load(); }} selectedDate={todayKey()} initialInitiativeId={initiativeId} />
  </Box>;
}
